import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { FacebookLogo, InstagramLogo, ArrowUpRight } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const fbValid = (url) => /facebook\.com\/.+/.test(url || "");
export const igCode = (url) => {
  const m = (url || "").match(/instagram\.com\/(p|reel)\/([\w-]+)/);
  return m ? m[2] : null;
};
export const igValid = (url) => !!igCode(url);

const fbEmbed = (url) => {
  const isVideo = /\/videos\/|\/reel\/|watch/.test(url);
  const base = isVideo
    ? "https://www.facebook.com/plugins/video.php"
    : "https://www.facebook.com/plugins/post.php";
  return `${base}?href=${encodeURIComponent(url)}&show_text=true&width=500`;
};

const LazyFrame = ({ src, title, testid }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { rootMargin: "300px" }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} className="h-[560px] bg-black/[0.04] rounded-2xl overflow-hidden">
      {visible && (
        <iframe
          data-testid={testid}
          src={src}
          title={title}
          loading="lazy"
          className="w-full h-full border-0"
          allow="encrypted-media; clipboard-write; picture-in-picture"
        />
      )}
    </div>
  );
};

const PostCard = ({ url, red, testid }) => {
  const valid = red === "facebook" ? fbValid(url) : igValid(url);
  const src =
    red === "facebook" ? (valid ? fbEmbed(url) : null) : valid ? `https://www.instagram.com/${url.includes("/reel/") ? "reel" : "p"}/${igCode(url)}/embed/` : null;
  return (
    <div data-testid={testid} className="min-w-[66%] sm:min-w-0 snap-start">
      {src ? (
        <LazyFrame src={src} title={`Publicación de ${red}`} testid={`${testid}-iframe`} />
      ) : (
        <div className="h-[560px] bg-black/[0.04] rounded-2xl flex items-center justify-center">
          <span className="text-black/30 text-xs">Publicación no disponible</span>
        </div>
      )}
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-2 inline-flex items-center gap-1 text-xs text-black/45 hover:text-black transition-colors"
      >
        Ver en {red === "facebook" ? "Facebook" : "Instagram"} <ArrowUpRight size={12} />
      </a>
    </div>
  );
};

const RedRow = ({ red, links, titulo, enlaceTxt, enlaceUrl }) => {
  const filled = links.filter((u) => (u || "").trim());
  if (filled.length === 0) return null;
  const Icon = red === "facebook" ? FacebookLogo : InstagramLogo;
  return (
    <div className="mb-10 last:mb-0">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-lg text-[#111418] flex items-center gap-2">
          <Icon size={20} weight="fill" className="text-[#FF5508]" /> {titulo}
        </h3>
        <a
          data-testid={`social-${red}-link`}
          href={enlaceUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm font-semibold text-[#FF5508] hover:text-black transition-colors inline-flex items-center gap-1"
        >
          {enlaceTxt} <ArrowUpRight size={14} />
        </a>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-2 snap-x sm:grid sm:grid-cols-3 sm:overflow-visible">
        {filled.map((u, i) => (
          <PostCard key={i} url={u.trim()} red={red} testid={`social-${red}-post-${i}`} />
        ))}
      </div>
    </div>
  );
};

export const SocialPosts = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios
      .get(`${API}/settings/social-posts`)
      .then((r) => setData(r.data))
      .catch(() => setData({ facebook: [], instagram: [] }));
  }, []);

  if (!data) return null;
  const hasFb = data.facebook?.some((u) => (u || "").trim());
  const hasIg = data.instagram?.some((u) => (u || "").trim());
  if (!hasFb && !hasIg) return null;

  return (
    <section id="siguenos" data-testid="social-posts" className="py-24 lg:py-32 border-t border-black/10 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <p className="text-xs font-semibold text-[#FF5508] uppercase tracking-widest mb-3">Redes sociales</p>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase font-semibold tracking-tight text-[#111418] mb-10">
          Síguenos
        </h2>
        <RedRow
          red="facebook"
          links={data.facebook || []}
          titulo="Facebook"
          enlaceTxt="Ruta 360 MX"
          enlaceUrl="https://www.facebook.com/share/18RRu62cYq/"
        />
        <RedRow
          red="instagram"
          links={data.instagram || []}
          titulo="Instagram"
          enlaceTxt="@ruta.deportiva.360"
          enlaceUrl="https://www.instagram.com/ruta.deportiva.360/"
        />
      </div>
    </section>
  );
};
